/**
 * Path mode filtering for visualization
 * Single implementation used by both CVEAttackVisualizer and ThreatVisualizerD3
 */

import { NODE_TYPES } from './constants';

/**
 * Path mode configuration
 * Defines which node types to keep and which to collapse for each mode
 */
const PATH_MODE_CONFIG = {
  full: {
    keep: [NODE_TYPES.ORG, NODE_TYPES.ASSET, NODE_TYPES.TECH, NODE_TYPES.CVE, NODE_TYPES.CWE, NODE_TYPES.CAPEC, NODE_TYPES.TTP, NODE_TYPES.APT],
    collapse: [],
  },
  attack: {
    keep: [NODE_TYPES.CVE, NODE_TYPES.CWE, NODE_TYPES.CAPEC, NODE_TYPES.TTP, NODE_TYPES.APT],
    collapse: [NODE_TYPES.ORG, NODE_TYPES.ASSET, NODE_TYPES.TECH],
  },
  threat: {
    keep: [NODE_TYPES.CVE, NODE_TYPES.TTP, NODE_TYPES.APT],
    collapse: [NODE_TYPES.ORG, NODE_TYPES.ASSET, NODE_TYPES.TECH, NODE_TYPES.CWE, NODE_TYPES.CAPEC],
  },
  direct: {
    keep: [NODE_TYPES.CVE, NODE_TYPES.APT],
    collapse: [NODE_TYPES.ORG, NODE_TYPES.ASSET, NODE_TYPES.TECH, NODE_TYPES.CWE, NODE_TYPES.CAPEC, NODE_TYPES.TTP],
  },
};

/**
 * Filter nodes and links based on path mode
 * Creates new links that skip collapsed nodes
 *
 * @param {Array} nodes - Array of node objects with { id, type, ... }
 * @param {Array} links - Array of link objects with { source, target, ... }
 * @param {string} mode - Path mode: 'full', 'attack', 'threat', or 'direct'
 * @returns {{ nodes: Array, links: Array }} - Filtered nodes and links
 */
export function applyPathModeFilter(nodes, links, mode) {
  // Full mode - no filtering needed
  if (mode === 'full' || !mode) {
    return { nodes, links };
  }

  const config = PATH_MODE_CONFIG[mode];
  if (!config) {
    return { nodes, links };
  }

  // Build node type map
  const nodeTypeMap = new Map();
  nodes.forEach(n => {
    nodeTypeMap.set(n.id, n.type);
  });

  // Filter nodes - keep only those in the keep list
  const filteredNodes = nodes.filter(n => config.keep.includes(n.type));
  const filteredNodeSet = new Set(filteredNodes.map(n => n.id));

  // Build adjacency for path finding
  const forwardLinks = new Map(); // source -> [targets]
  links.forEach(l => {
    const sourceId = typeof l.source === 'object' ? l.source.id : l.source;
    const targetId = typeof l.target === 'object' ? l.target.id : l.target;
    if (!forwardLinks.has(sourceId)) {
      forwardLinks.set(sourceId, []);
    }
    forwardLinks.get(sourceId).push(targetId);
  });

  // Create new links that skip collapsed nodes
  const newLinks = [];
  const linkSet = new Set();

  // For each kept node, find connections to other kept nodes
  filteredNodes.forEach(node => {
    // Find forward connections (skip collapsed nodes)
    const findForwardConnections = (startNode, visited = new Set()) => {
      const targets = forwardLinks.get(startNode) || [];
      targets.forEach(target => {
        if (visited.has(target)) return;
        visited.add(target);

        if (filteredNodeSet.has(target)) {
          // Direct connection to a kept node
          const linkKey = `${node.id}|${target}`;
          if (!linkSet.has(linkKey)) {
            linkSet.add(linkKey);
            newLinks.push({ source: node.id, target, value: 1 });
          }
        } else {
          // Collapsed node - continue searching
          findForwardConnections(target, visited);
        }
      });
    };

    findForwardConnections(node.id);
  });

  return { nodes: filteredNodes, links: newLinks };
}

/**
 * Get the configuration for a specific path mode
 * @param {string} mode - Path mode
 * @returns {Object} - Configuration with keep and collapse arrays
 */
export function getPathModeConfig(mode) {
  return PATH_MODE_CONFIG[mode] || PATH_MODE_CONFIG.full;
}
